import { createSlice } from '@reduxjs/toolkit';

// Load draft from localStorage if available
const loadDraftFromStorage = () => {
  try {
    const savedDraft = localStorage.getItem('categoryDraftFormData');
    return savedDraft ? JSON.parse(savedDraft) : null;
  } catch (error) {
    console.error('Error loading draft from localStorage:', error);
    return null;
  }
};

const initialState = {
  categories: [],
  currentCategory: null,
  isLoading: false,
  error: null,
  draftFormData: loadDraftFromStorage(), // Load draft from localStorage on init
  filters: {
    search: '',
    isActive: null,
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
};

const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCategory: (state) => {
      state.currentCategory = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setLimit: (state, action) => {
      state.pagination.limit = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1; // Reset to first page when changing page size
    },
    resetPagination: (state) => {
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      };
    },
    // Save draft form data
    saveCategoryDraft: (state, action) => {
      state.draftFormData = action.payload;
      // Also save to localStorage for persistence across page refreshes
      try {
        localStorage.setItem('categoryDraftFormData', JSON.stringify(action.payload));
      } catch (error) {
        console.error('Error saving draft to localStorage:', error);
      }
    },
    // Clear draft form data
    clearCategoryDraft: (state) => {
      state.draftFormData = null;
      // Also remove from localStorage
      try {
        localStorage.removeItem('categoryDraftFormData');
      } catch (error) {
        console.error('Error clearing draft from localStorage:', error);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Categories
      .addCase('category/getCategories/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('category/getCategories/fulfilled', (state, action) => {
        state.isLoading = false;
        
        // Handle both array response and paginated response
        if (Array.isArray(action.payload)) {
          state.categories = action.payload;
        } else if (action.payload.data) {
          // Paginated response with nested data and pagination objects
          state.categories = action.payload.data;
          if (action.payload.pagination) {
            state.pagination = {
              page: action.payload.pagination.page || state.pagination.page,
              limit: action.payload.pagination.limit || state.pagination.limit,
              total: action.payload.pagination.total || 0,
              totalPages: action.payload.pagination.totalPages || 0,
              hasNext: action.payload.pagination.hasNext || false,
              hasPrev: action.payload.pagination.hasPrev || false,
            };
          }
        } else {
          state.categories = action.payload;
        }
        
        state.error = null;
      })
      .addCase('category/getCategories/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch categories';
      })
      // Get Categories (without params)
      .addCase('category/getCategoriesWithoutParams/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('category/getCategoriesWithoutParams/fulfilled', (state, action) => {
        state.isLoading = false;
        if (Array.isArray(action.payload)) {
          state.categories = action.payload;
        } else if (action.payload?.data) {
          state.categories = action.payload.data;
        } else {
          state.categories = action.payload || [];
        }
        state.error = null;
      })
      .addCase('category/getCategoriesWithoutParams/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch categories';
      })
       // Get Active Categories (without params)
      .addCase('category/getActiveCategories/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('category/getActiveCategories/fulfilled', (state, action) => {
        state.isLoading = false;
        if (Array.isArray(action.payload)) {
          state.categories = action.payload;
        } else if (action.payload?.data) {
          state.categories = action.payload.data;
        } else {
          state.categories = action.payload || [];
        }
        state.error = null;
      })
      .addCase('category/getActiveCategories/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch categories';
      })
      // Get Category by ID
      .addCase('category/getCategoryById/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('category/getCategoryById/fulfilled', (state, action) => {
        state.isLoading = false;
        state.currentCategory = action.payload;
        state.error = null;
      })
      .addCase('category/getCategoryById/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch category';
      })
      // Create Category
      .addCase('category/createCategory/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('category/createCategory/fulfilled', (state, action) => {
        state.isLoading = false;
        state.categories.unshift(action.payload); // Add new category to the beginning
        state.error = null;
      })
      .addCase('category/createCategory/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create category';
      })
      // Update Category
      .addCase('category/updateCategory/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('category/updateCategory/fulfilled', (state, action) => {
        state.isLoading = false;
        const index = state.categories.findIndex(cat => cat._id === action.payload._id);
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
        state.error = null;
      })
      .addCase('category/updateCategory/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update category';
      })
      // Delete Category
      .addCase('category/deleteCategory/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('category/deleteCategory/fulfilled', (state, action) => {
        state.isLoading = false;
        state.categories = state.categories.filter(cat => cat._id !== action.payload);
        state.error = null;
      })
      .addCase('category/deleteCategory/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete category';
      });
  },
});

export const { 
  clearError, 
  clearCurrentCategory, 
  setFilters, 
  setPagination, 
  setPage, 
  setLimit, 
  setCurrentPage, 
  setItemsPerPage, 
  resetPagination,
  saveCategoryDraft,
  clearCategoryDraft
} = categorySlice.actions;

export default categorySlice.reducer;

